import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { MAIN_SITE_URL } from '@/mocks/categories';
import { articles, type ArticleContent } from '@/mocks/articles';

const SITE_URL = import.meta.env.VITE_SITE_URL;

type Lang = 'en' | 'pl' | 'es' | 'fr';

export default function ArticlePage() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const lang = (i18n.language as Lang) || 'en';
  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [slug]);

  const content: ArticleContent | undefined = article
    ? article.content[lang] ?? article.content.en
    : undefined;

  if (!article || !content) {
    return (
      <>
        <Navbar />
        <main className="pt-28 pb-16 min-h-[60vh]">
          <div className="mx-auto max-w-3xl px-4 md:px-6 text-center">
            <h1 className="font-heading font-bold text-foreground-950 text-2xl md:text-3xl mb-4">
              {t('article.notFound')}
            </h1>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              {t('article.backHome')}
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: content.title,
        description: content.metaDesc,
        datePublished: article.date,
        dateModified: '2026-08-20',
        author: { '@type': 'Organization', name: 'USFansSheet Editorial Team' },
        publisher: { '@type': 'Organization', name: 'USFansSheet.help' },
        mainEntityOfPage: `${SITE_URL}/guides/${article.slug}`,
      },
      {
        '@type': 'FAQPage',
        mainEntity: content.faq.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ],
  };

  return (
    <>
      <Navbar />
      <main className="pt-24 md:pt-28">
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />

        <article className="mx-auto max-w-3xl px-4 md:px-6 py-8 md:py-12">
          <nav className="mb-6 text-sm">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-primary-700 font-medium hover:underline whitespace-nowrap"
            >
              <span className="flex items-center justify-center w-4 h-4">
                <i className="ri-arrow-left-line text-sm" />
              </span>
              {t('article.backHome')}
            </Link>
          </nav>

          <header className="mb-8">
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-accent-100 text-accent-900 text-xs font-semibold mb-4 whitespace-nowrap">
              {t(article.categoryKey)}
            </span>
            <h1 className="font-heading font-bold text-foreground-950 text-2xl md:text-4xl leading-tight mb-4">
              {content.title}
            </h1>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-4">
              {content.summary}
            </p>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-calendar-line text-base" />
                {t('article.updated')}: {article.date}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-time-line text-base" />
                {article.readTime} {t('article.minRead')}
              </span>
            </div>
          </header>

          <div className="mb-8 bg-accent-100/70 border border-accent-300/50 rounded-lg p-5 md:p-6">
            <h2 className="font-heading font-semibold text-foreground-900 text-base md:text-lg mb-2">
              {t('article.quickAnswerTitle')}
            </h2>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed">{content.quickAnswer}</p>
          </div>

          <div className="space-y-10">
            {content.sections.map((s) => (
              <section key={s.h}>
                <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl mb-3">
                  {s.h}
                </h2>
                <p className="text-foreground-700 text-sm md:text-base leading-relaxed">{s.b}</p>
              </section>
            ))}
          </div>

          <section className="mt-12">
            <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl mb-6">
              {t('article.faqTitle')}
            </h2>
            <div className="space-y-3">
              {content.faq.map((item, idx) => {
                const isOpen = openFaq === idx;
                return (
                  <div key={item.q} className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setOpenFaq(isOpen ? null : idx)}
                      className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left cursor-pointer"
                    >
                      <span className="font-heading font-semibold text-foreground-900 text-sm md:text-base">
                        {item.q}
                      </span>
                      <span className={`flex items-center justify-center w-6 h-6 text-foreground-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}>
                        <i className="ri-arrow-down-s-line text-lg" />
                      </span>
                    </button>
                    {isOpen && (
                      <div className="px-5 pb-5 text-foreground-600 text-sm md:text-base leading-relaxed">{item.a}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          <section className="mt-12 bg-background-100 border border-background-200 rounded-lg p-6">
            <h2 className="font-heading font-semibold text-foreground-900 text-lg mb-4">{t('article.relatedTitle')}</h2>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="inline-flex items-center gap-2 text-primary-700 text-sm hover:underline whitespace-nowrap">
                  <i className="ri-home-5-line text-base" />
                  {t('article.related1')}
                </Link>
              </li>
              <li>
                <Link to="/#categories" className="inline-flex items-center gap-2 text-primary-700 text-sm hover:underline whitespace-nowrap">
                  <i className="ri-grid-line text-base" />
                  {t('article.related2')}
                </Link>
              </li>
            </ul>
          </section>

          <section className="mt-12 text-center bg-primary-500 rounded-lg p-8 md:p-10">
            <h2 className="font-heading font-bold text-background-50 text-xl md:text-2xl mb-2">
              {t('article.ctaTitle')}
            </h2>
            <p className="text-background-50/90 text-sm md:text-base mb-6">{t('article.ctaBody')}</p>
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-7 py-3 rounded-lg bg-background-50 text-foreground-950 font-semibold text-sm md:text-base hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              {t('article.cta')}
              <span className="flex items-center justify-center w-5 h-5">
                <i className="ri-external-link-line text-base" />
              </span>
            </a>
          </section>
        </article>
      </main>
      <Footer />
    </>
  );
}